import { FC, JSX, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ColumnConfig, SortDirections, TextAlignment, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { getGenericErrorMessage } from "@app/shared/utility/getGenericErrorMessage";
import { formatDateCustom } from "@app/shared/formatDateTime";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { useRegulatorNetworks } from "@app/compliance-connect/regulator/hooks/useRegulatorNetworks";
import { ComplianceConnectApiFilters, ComplianceConnectFiltersContext } from "@app/compliance-connect/regulator/contexts/ComplianceConnectFiltersContext";
import { ComplianceConnectNetwork, SelectedNetwork } from "@app/compliance-connect/regulator/complianceConnectTypes";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { RegionFilterItem } from "@app/projects/adequacySharedProjectContextProps";
import { FilterType, NetworkAssessmentAnalysisType, ProjectPerspective } from "@app/projects/enumerators";
import { NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { ReportCustomizationRouteState } from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ProgressBar } from "@app/shared/ProgressBar";
import { QesColors } from "@app/shared/qes-standards/colors";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 100, 1000];
const MAX_SELECTED_NETWORKS = 5;

interface ComplianceConnectNetworksTableWaffleToppings {
    networkName: JSX.Element;
    issuerName: string;
    totalGapCount: number;
    fillableGapCount: number;
    lastUpdatedDate: string;
    countyCount: number;
    networkType: string;
    adequacyIndex: string;
    adequacy: JSX.Element;
    key: string;
}

const backendSortColumnMap: Partial<Record<keyof ComplianceConnectNetworksTableWaffleToppings, string>> = {
    adequacyIndex: "adequacyIndex",
    countyCount: "countyCount",
    fillableGapCount: "fillableGapCount",
    issuerName: "issuerName",
    lastUpdatedDate: "lastUpdatedDate",
    networkName: "networkName",
    networkType: "networkType",
    totalGapCount: "totalGapCount",
};

const columnConfig: ColumnConfig = {
    /* oxlint-disable sort-keys */
    networkName: { displayText: "Network Name", testId: "network-name" },
    issuerName: { displayText: "Issuer", testId: "issuer-name" },
    totalGapCount: { alignment: TextAlignment.Right, displayText: "Total Gap Count", testId: "total-gap-count" },
    fillableGapCount: { alignment: TextAlignment.Right, displayText: "Fillable Gap Count", testId: "fillable-gap-count" },
    lastUpdatedDate: { displayText: "Last Updated", testId: "last-updated-date" },
    countyCount: { alignment: TextAlignment.Right, displayText: "County Count", testId: "county-count" },
    networkType: { displayText: "Network Type", testId: "network-type" },
    adequacyIndex: { alignment: TextAlignment.Right, displayText: "Adequacy Index", testId: "adequacy-index" },
    adequacy: { displayText: "Adequacy", minWidth: "180px", noSort: true, testId: "adequacy" },
    /* oxlint-enable sort-keys */
};

const buildComplianceConnectNetworksTableData = (networks: ComplianceConnectNetwork[]): ComplianceConnectNetworksTableWaffleToppings[] =>
    networks.map((n) => {
        /* oxlint-disable sort-keys */
        return {
            networkName: <Link to={`/compliance-connect/network/${n.networkId}`}>{n.networkName}</Link>,
            issuerName: n.issuerName,
            totalGapCount: n.totalGapCount,
            fillableGapCount: n.fillableGapCount,
            lastUpdatedDate: formatDateCustom(n.lastUpdatedDate, "MMM D, YYYY h:mm A"),
            countyCount: n.countyCount,
            networkType: n.networkType,
            adequacyIndex: n.adequacyIndex.toFixed(1),
            adequacy: (
                <ProgressBar
                    bands={[
                        { color: QesColors.QesTertiaryGreen2, colorType: "hex", outOfScale: false, percent: n.percentPassBoth },
                        { color: QesColors.QesTertiaryYellow2, colorType: "hex", outOfScale: false, percent: n.percentPassOne },
                        { color: QesColors.QesTertiaryRed2, colorType: "hex", outOfScale: false, percent: n.percentPassNone },
                        { color: QesColors.Blue500, colorType: "hex", outOfScale: false, percent: n.percentPassByException },
                    ]}
                    isGrayscale={false}
                />
            ),
            [wafflesKeyColumnName]: n.networkId,
        };
        /* oxlint-enable sort-keys */
    });

// ReportCustomizationContext's state-summary loader only supports FilterType.State (and handles multiple
// selections), whereas its FilterType.County handling only ever honors a single match. Since Compliance
// Connect's service area filter is a multi-select of counties, roll the selection up to the parent states
// here rather than widening the shared county-matching logic to a multi-select case it wasn't built for.
const buildCreateReportFilters = (apiReadyFilters: ComplianceConnectApiFilters, serviceAreaOptions: NestingParentDropdownItem[]): RegionFilterItem[] => {
    const selectedCountyKeys = new Set(apiReadyFilters.serviceAreaIds);
    const selectedStateAbbreviations = serviceAreaOptions
        .filter((state) => state.children.some((county) => selectedCountyKeys.has(county.key)))
        .map((state) => state.key);

    const serviceAreaFilters: RegionFilterItem[] = selectedStateAbbreviations.map((abbreviation) => ({
        filterType: FilterType.State,
        filterValue: abbreviation,
        urlPart: `${FilterType.State}${encodeURIComponent(abbreviation)}`,
    }));

    const specialtyFilters: RegionFilterItem[] = apiReadyFilters.specialtyGroupCodes.map((code) => ({
        filterType: FilterType.Specialty,
        filterValue: code,
        urlPart: `${FilterType.Specialty}${encodeURIComponent(code)}`,
    }));

    return [...serviceAreaFilters, ...specialtyFilters];
};

const ComplianceConnectNetworksTable: FC = () => {
    const filtersContext = useContext(ComplianceConnectFiltersContext);
    const { enabledFeatureFlags } = useContext(FeatureFlagContext);

    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
    const [sortDescending, setSortDescending] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const [selectedNetworksMap, setSelectedNetworksMap] = useState<Map<string, ComplianceConnectNetwork>>(new Map());

    useEffect(() => {
        setPageIndex(1);
        setSelectedKeys([]);
        setSelectedNetworksMap(new Map());
    }, [filtersContext.apiReadyFilters]);

    const { regulatorNetworks, regulatorNetworksError, isLoading, totalCount } = useRegulatorNetworks({
        pageIndex: pageIndex - 1,
        pageSize,
        sortColumn,
        sortDescending,
    });

    const networks = regulatorNetworks ?? [];

    const tableData = useMemo(() => buildComplianceConnectNetworksTableData(networks), [networks]);

    const handlePassThroughSort = useCallback((
        rows: ComplianceConnectNetworksTableWaffleToppings[],
        column: keyof ComplianceConnectNetworksTableWaffleToppings,
        direction: SortDirections,
    ): ComplianceConnectNetworksTableWaffleToppings[] => {
        if (direction === SortDirections.None) {
            setSortColumn(undefined);
            setSortDescending(false);
        } else {
            setSortColumn(backendSortColumnMap[column]);
            setSortDescending(direction === SortDirections.Desc);
        }
        setPageIndex(1);
        return rows;
    }, []);

    const handleCheckboxChange = useCallback((keys: string[]) => {
        setSelectedKeys(keys);
        setSelectedNetworksMap(prevMap => {
            const nextMap = new Map(prevMap);
            keys.forEach(key => {
                const match = networks.find(n => n.networkId === key);
                if (match) nextMap.set(key, match);
            });
            return nextMap;
        });
    }, [networks]);

    const selectedNetworks: SelectedNetwork[] = useMemo(
        () => selectedKeys
            .map(key => selectedNetworksMap.get(key))
            .filter((n): n is ComplianceConnectNetwork => n != null)
            .map(n => ({ networkId: n.networkId, networkName: n.networkName })),
        [selectedKeys, selectedNetworksMap],
    );

    const selectedNetwork = selectedKeys.length === 1 ? selectedNetworksMap.get(selectedKeys[0]) ?? null : null;

    const createReportUrl = selectedNetwork
        ? `/projects/${selectedNetwork.customProjectId}/adequacy/export/${selectedNetwork.projectRunConfigurationId}/true`
        : null;

    const createReportState: ReportCustomizationRouteState | null = selectedNetwork
        ? {
            adequacyFilters: {
                active: true,
                all: false,
                approved: false,
                exceptions: false,
                gaps: false,
                opportunity: false,
                pending: false,
                viewBy: ProjectPerspective.ByCounty,
            },
            defaultReportType: ReportType.StateSummary,
            filters: buildCreateReportFilters(filtersContext.apiReadyFilters, filtersContext.serviceAreaOptions),
            networkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea,
            primaryMembership: {
                alias: selectedNetwork.memberSetAlias,
                id: selectedNetwork.memberSetId,
            },
            projectName: selectedNetwork.projectName,
            selectedMemberships: [selectedNetwork.memberSetId],
            selectedTimeslice: selectedNetwork.projectRunConfigurationId,
            showAdvancedComparison: selectedNetwork.showAdvancedComparison,
        }
        : null;

    const paginationProps: WafflesPaginationProps = useMemo(() => ({
        changePage: (pageNumber: number) => setPageIndex(pageNumber),
        enableBackendPagination: true,
        handlePageSizeChange: (size: number) => {
            setPageSize(size);
            setPageIndex(1);
        },
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        recordsPerPage: pageSize,
        resetToFirstPage: false,
        showPageSizeSelector: true,
        totalRecords: totalCount,
    }), [pageSize, totalCount]);

    const startRecord = (pageIndex - 1) * pageSize + 1;

    const endRecord = Math.min(pageIndex * pageSize, totalCount);

    if (regulatorNetworksError) return <QESAlert type="danger" persist>{getGenericErrorMessage()}</QESAlert>;

    return (
        <>
            <p>Displaying {totalCount > 0 ? `${startRecord}-${endRecord} of ${totalCount}` : "0"} records</p>
            <Waffles<ComplianceConnectNetworksTableWaffleToppings>
                bulkActionBar={
                    <span className="bulk-action-group d-inline-flex align-items-center gap-3">
                        {enabledFeatureFlags.has(Features.ADEQUACY_REPORT_CUSTOMIZATION) ? (
                            createReportUrl ? (
                                <Link<ReportCustomizationRouteState>
                                    className="nav-link"
                                    data-testid="compliance-connect-networks__create-report"
                                    to={{ pathname: createReportUrl, state: createReportState }}
                                >
                                    <QesIcon iconName={QesIcons.DownloadCloudV1} isDecorative className="align-text-top me-1" />Create Report
                                </Link>
                            ) : (
                                <span className="nav-link text-muted" data-testid="compliance-connect-networks__create-report--disabled">
                                    <QesIcon iconName={QesIcons.DownloadCloudV1} isDecorative className="align-text-top me-1" />Create Report
                                </span>
                            )
                        ) : null}
                        {selectedKeys.length >= 2 && selectedKeys.length <= MAX_SELECTED_NETWORKS ? (
                            <Link<{ selectedNetworks: SelectedNetwork[] }>
                                className="nav-link"
                                data-testid="compliance-connect-networks__compare-networks"
                                to={{ pathname: "/compliance-connect/network-comparison", state: { selectedNetworks } }}
                            >
                                <QesIcon iconName={QesIcons.SwitchHorizontalV1} isDecorative className="align-text-top me-1" />Compare Networks
                            </Link>
                        ) : (
                            <span className="nav-link text-muted" data-testid="compliance-connect-networks__compare-networks--disabled">
                                <QesIcon iconName={QesIcons.SwitchHorizontalV1} isDecorative className="align-text-top me-1" />Compare Networks
                            </span>
                        )}
                    </span>
                }
                columns={columnConfig}
                customSort={handlePassThroughSort}
                data={tableData}
                enablePagination={true}
                fullWidth={true}
                handleCheckboxChange={handleCheckboxChange}
                loading={isLoading}
                paginationProps={paginationProps}
                passThroughSorting={true}
                selectedKeys={selectedKeys}
                showCheckboxes={true}
                testId="compliance-connect-networks-table"
            />
        </>
    );
};

export default ComplianceConnectNetworksTable;
